/* eslint-disable no-param-reassign */
/* eslint-disable import/prefer-default-export */
import {
    type Product360Directory,
    type ProductImage,
    type ProductInformationViewModel,
    ProductMediaContentDiscriminator as ProductInformationViewModelproductMediaDiscriminator,
    type ProductVideoModel
} from 'autogen/swagger/all';
import type {
    GalleryMediaItems,
    HighlightedVideoMediaItem,
    ImageMediaItem,
    Product360MediaItem,
    VideoMediaItem
} from 'components/GalleryMediaPreviewer/shared/types';

function isHighlightedVideoMediaItem(
    mediaItem: Partial<HighlightedVideoMediaItem>
): mediaItem is HighlightedVideoMediaItem {
    if (!mediaItem.video || !mediaItem.thumbnail || !mediaItem.original || mediaItem.type !== 'highlightedVideo') {
        return false;
    }

    return true;
}

export const prependHighlightedVideoMediaItemToMediaItems = (items: GalleryMediaItems): GalleryMediaItems => {
    const highlightedVideoMediaItem = items.reduce<Partial<HighlightedVideoMediaItem>>(
        (result, curr) => {
            let returnResult = result;

            if (curr.type === 'video' && curr.productVideoType === 'Review') {
                returnResult = { ...result, video: curr.video };
            }

            if (curr.type === 'image' && curr.productImageType === 'PrimaryImage') {
                returnResult.thumbnail = curr.thumbnail;
                returnResult.original = curr.original;
            }

            return returnResult;
        },
        { type: 'highlightedVideo' }
    );

    if (isHighlightedVideoMediaItem(highlightedVideoMediaItem)) {
        items.unshift(highlightedVideoMediaItem);
        return items;
    }

    return items;
};

/* MAPPERS */
export const mapAutoplayVideosToMediaItems = (items: GalleryMediaItems): GalleryMediaItems => {
    const videosMediaItems: VideoMediaItem[] = [];
    const restMediaItems: GalleryMediaItems = [];

    items.forEach((mediaItem) => {
        if (mediaItem.type === 'video') return videosMediaItems.push(mediaItem);
        return restMediaItems.push(mediaItem);
    });

    // Attach videos that will autoplay to each VideoMediaItem
    const videoMediaItemsWithAutoplay: VideoMediaItem[] = videosMediaItems.map(
        ({ video: currVideo, ...videoMediaItem }) => ({
            ...videoMediaItem,
            video: {
                ...currVideo,
                autoplayVideos: videosMediaItems.filter((x) => currVideo.url !== x.video.url).map((y) => y.video)
            }
        })
    );

    return restMediaItems.concat(videoMediaItemsWithAutoplay);
};

export const mapProductVideoModelToVideoMediaItem = (item: ProductVideoModel): VideoMediaItem => ({
    type: 'video',
    video: {
        id: item.video.id,
        url: item.linkAction.url,
        platform: item.video.platform,
        isVideoly: item.video.isVideoly,
        thumbnailUrl: item.video.thumbnailUrl,
        tag: item.video.tag
    },
    productVideoType: item.video.type
});

export const mapProductImageToImageMediaItem = (item: ProductImage): ImageMediaItem => ({
    type: 'image',
    original: item.url,
    thumbnail: item.url,
    productImageType: item.type
});

export const mapProduct360DirectoryToProduct360MediaItem = (item: Product360Directory): Product360MediaItem => ({
    type: 'product360',
    directoryUrl: item.url,
    thumbnail: item.previewUrl
});

export const mapProductInformationModelToGalleryMediaItems = (items: ProductInformationViewModel['productMedia']) => {
    const mediaItems: GalleryMediaItems = [];

    items.forEach((item) =>
        ProductInformationViewModelproductMediaDiscriminator(
            item,
            (videoItem) => mediaItems.push(mapProductVideoModelToVideoMediaItem(videoItem)),
            (product360Item) => mediaItems.push(mapProduct360DirectoryToProduct360MediaItem(product360Item)),
            (imageItem) => mediaItems.push(mapProductImageToImageMediaItem(imageItem)),
            () => null
        )
    );

    return mediaItems;
};
